import { SelectOption } from '@models/shared.model';
import { AutomationFieldType } from '@models/automation-rules';

/** How an input standing for all values of its field reads. */
export const AUTOMATION_WILDCARD_LABEL = 'All';

/** Explanation shown next to the wildcard entry of a dropdown. */
export const AUTOMATION_WILDCARD_DESCRIPTION =
    'Applies to every value of this field';

/**
 * Value the wildcard entry of a dropdown carries. The dropdown holds plain
 * option values, so the wildcard needs one of its own that no real option can
 * ever take: an option of the api is an id or a name, and neither can hold the
 * leading null character, which is also nothing a user can type. An option
 * genuinely named "All" therefore stays selectable next to the wildcard.
 *
 * The value never leaves the editor: a rule is sent with `isWildcard: true`
 * and an empty value instead.
 */
export const AUTOMATION_WILDCARD_VALUE = '\u0000automation-wildcard';

/** The wildcard entry, kept below the real values of a dropdown. */
export const AUTOMATION_WILDCARD_OPTION: SelectOption = {
    value: AUTOMATION_WILDCARD_VALUE,
    viewValue: AUTOMATION_WILDCARD_LABEL,
};

/**
 * Whether the editor value of a field stands for all values of the field.
 *
 * A selection is asked about while it is being made as well, where the
 * wildcard may sit next to the values it is about to replace, so holding it is
 * enough. A selection that has settled carries the wildcard on its own, which
 * is what {@link resolveWildcardSelection} leaves behind.
 */
export function isAutomationWildcardValue(value: unknown): boolean {
    return Array.isArray(value)
        ? value.includes(AUTOMATION_WILDCARD_VALUE)
        : value === AUTOMATION_WILDCARD_VALUE;
}

/**
 * Whether a typed value is the keyword standing for all values of its field.
 * Only the keyword on its own counts, so a sentence holding the word stays the
 * text the user meant to store.
 */
export function isAutomationWildcardKeyword(value: unknown): boolean {
    return (
        typeof value === 'string' &&
        value.trim().toLocaleLowerCase() ===
            AUTOMATION_WILDCARD_LABEL.toLocaleLowerCase()
    );
}

/**
 * The field types whose editor is typed into, so the keyword is the only way
 * their input can stand for all values. A dropdown is left out: it offers the
 * wildcard as an entry and an option genuinely named "All" stays selectable. A
 * date is left out as well, because it is picked rather than typed.
 */
export function takesAutomationWildcardKeyword(
    type: AutomationFieldType,
): boolean {
    return (
        type === AutomationFieldType.FreeText ||
        type === AutomationFieldType.Number ||
        type === AutomationFieldType.Integer
    );
}

/**
 * Whether an input of a rule stands for all values of its field, however the
 * type of the field lets the user say so: a dropdown carries the wildcard as
 * a value, while a typed input takes the keyword.
 */
export function isAutomationWildcardInput(
    type: AutomationFieldType,
    value: unknown,
): boolean {
    return takesAutomationWildcardKeyword(type)
        ? isAutomationWildcardKeyword(value)
        : isAutomationWildcardValue(value);
}

/**
 * The selection of a multi select as either the wildcard or concrete values,
 * never both: taking the wildcard drops the values selected before it, while
 * taking a value drops the wildcard it is selected on top of.
 */
export function resolveWildcardSelection(
    selection: (string | number)[],
    hadWildcard: boolean,
): (string | number)[] {
    if (!selection.includes(AUTOMATION_WILDCARD_VALUE)) return selection;

    const values = selection.filter(
        (value) => value !== AUTOMATION_WILDCARD_VALUE,
    );

    return hadWildcard && values.length > 0
        ? values
        : [AUTOMATION_WILDCARD_VALUE];
}
